import { ArrowLeftOutlined, DollarOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Input,
  Popconfirm,
  Row,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { IAllInvoices } from '../../../common/types/commonInterfaces';
import { IPermission, crud } from '../../../common/types/commonTypes';
import {
  TableParams,
  filterNullColumns,
  formatAmount,
  rangePresets,
  separateClient,
} from '../../../common/utils/common.utils';
import InvoiceOtherAddMoneyModal from '../../../modules/Invoice(Other)/Modals/InvoiceOtherAddMoneyModal';
import Reload from '../reload/Reload';
import { loadingIndicator } from '../spinner/LoadingIndicator';

type Props = {
  data?: IAllInvoices[];
  trashData?: IAllInvoices[] | undefined;
  setIsTrash: React.Dispatch<React.SetStateAction<boolean>>;
  viewLink: string;
  editLink: string;
  createLink: string;
  permission: IPermission | undefined;
  addMRPermission: ['*'] | undefined;
  isTrash: boolean;
  handleOnDelete: (id: number, index: number) => void;
  setDataSource: React.Dispatch<
    React.SetStateAction<IAllInvoices[] | undefined>
  >;
  InvocieModalTitle?: string | undefined;
  dataSource?: IAllInvoices[];
  isLoading: boolean;
  refetch: ({ query }: { query?: string }) => void;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  setCount: React.Dispatch<React.SetStateAction<number>>;
  count: number;
  setDate: React.Dispatch<React.SetStateAction<never[]>>;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  deleteLoading: boolean;
  columnIndex: number | null;
};

const InvoiceListHeader = ({
  viewLink,
  editLink,
  createLink,
  permission,
  InvocieModalTitle,
  handleOnDelete,
  dataSource,
  isLoading,
  addMRPermission,
  refetch,
  count,
  setQuery,
  setDate,
  setSearch,
  columnIndex,
}: Props) => {
  const location = useLocation();
  const [tableParams, setTableParams] = React.useState<TableParams>({
    pagination: {
      current: 1,
      defaultPageSize: 50,
      showSizeChanger: true,
      pageSizeOptions: ['50', '100', '200', '500'],
      pageSize: 50,
      total: count,
    },
  });
  React.useEffect(() => {
    setTableParams((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, total: count },
    }));
  }, [count]);

  const columns: ColumnsType<IAllInvoices> = [
    {
      title: 'SL.',
      width: 55,
      render: (_, data, index) => (
        <>
          {((tableParams?.pagination?.current || 1) - 1) *
            (tableParams?.pagination?.pageSize || 50) +
            1 +
            index}
        </>
      ),
    },
    {
      title: 'Invoice No',
      dataIndex: 'invoice_no',
      key: 'invoice_no',
      width: 100,
    },

    {
      title: 'Sales Date',
      key: 'invoice_date',
      dataIndex: 'invoice_date',

      render: (_, data) => (
        <>{moment(data?.invoice_date).format('DD MMM YYYY')} </>
      ),
      width: 135,
      align: 'center',
    },
    {
      title: 'Client Name',
      dataIndex: 'client_name',
      key: 'client_name',
      render: (value, record) => {
        const { client_id, combined_id } = separateClient(record?.comb_client);

        return record?.comb_client ? (
          <Link
            to={
              client_id
                ? `/reports/client_ledger?id=${client_id}`
                : `/reports/combined_ledger?id=${combined_id}`
            }
          >
            <span>
              {record.client_name} / {record.client_entry_id}
            </span>
          </Link>
        ) : (
          <></>
        );
      },
      width: 150,
    },
    {
      title: 'Sales Price',
      dataIndex: 'net_total',
      key: 'net_toal',
      width: 120,
      align: 'right',
      sorter: (a, b) => Number(a.net_total) - Number(b.net_total),

      render: (curr) => (
        <span className='color-sales'>{formatAmount(curr)}</span>
      ),
    },
    {
      title: 'Rec Amount',
      dataIndex: 'invclientpayment_amount',
      key: 'invclientpayment_amount',
      align: 'right',
      width: 120,
      render: (curr) => (
        <span className='color-collection'>{formatAmount(curr)}</span>
      ),
    },
    {
      title: 'Due Amount',
      align: 'right',
      width: 120,
      render(_, record) {
        const dueAmount =
          Number(record?.net_total) - Number(record?.invclientpayment_amount);

        return dueAmount > 0 ? (
          <span style={{ color: 'red' }}>
            {formatAmount(
              Number(record?.net_total) -
                Number(record?.invclientpayment_amount)
            )}
          </span>
        ) : (
          <Tag color='cyan' icon={<DollarOutlined />}>
            PAID
          </Tag>
        );
      },
      sorter: (a, b) => Number(a.net_total) - Number(b.net_total),
    },
    {
      title: 'MR. No.',
      dataIndex: 'money_receipt_num',
      key: 'money_receipt_num',
      width: 120,
      render: (_, data) => (
        <span>
          {data?.money_receipt_num
            ?.split(',')
            .slice(0, 3)
            .map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          {data?.money_receipt_num?.split(',').length > 3 && <span>...</span>}
        </span>
      ),
    },

    {
      title: 'Sales by',
      dataIndex: 'sales_by',
      key: 'sales_by',
      width: 150,
    },

    {
      title: 'Action',
      key: 'operation',
      width: 200,

      render: (_, data, index) => {
        console.log(viewLink);
        return (
          <Space size='small'>
            {permission?.['read:any'] && (
              <Link
                to={viewLink + '/' + data?.invoice_id}
                state={location.pathname}
              >
                <Button size='small' type='primary'>
                  <Typography style={{ color: '#ffffff' }}>View</Typography>
                </Button>
              </Link>
            )}

            {!data?.invoice_is_reissued &&
              permission?.[crud.update] &&
              !data?.invoice_is_refund && (
                <Link
                  to={editLink + '/' + data?.invoice_id}
                  state={location.pathname}
                >
                  <Button size='small' type='primary'>
                    Edit
                  </Button>
                </Link>
              )}

            {!data?.invoice_is_reissued &&
              permission?.['delete:any'] &&
              !data?.invoice_is_refund && (
                <>
                  {permission?.['delete:any'] && (
                    <Button
                      size='small'
                      type='primary'
                      danger
                      loading={columnIndex === index && true}
                    >
                      <Popconfirm
                        onConfirm={() =>
                          handleOnDelete(data?.invoice_id, index)
                        }
                        title='Sure to delete?'
                      >
                        <Typography style={{ color: '#ffffff' }}>
                          Delete
                        </Typography>
                      </Popconfirm>
                    </Button>
                  )}
                </>
              )}

            {addMRPermission && !data?.invoice_is_refund && (
              <InvoiceOtherAddMoneyModal
                invoice_id={data?.invoice_id}
                InvocieModalTitle={InvocieModalTitle}
                disabled={
                  Number(data?.net_total) <=
                  Number(data?.invclientpayment_amount)
                }
              />
            )}
          </Space>
        );
      },
    },
  ];

  if (location.pathname === '/invoiceother')
    columns.splice(8, 0, {
      title: 'Passport No',
      dataIndex: 'passport_no',
      key: 'passport_no',
      width: 100,
      render: (_, data) => (
        <span>
          {data?.passport_no?.split(',').map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </span>
      ),
    });

  // Handle Search Operation

  const handleDateRangeChange = (date: any, dateString: any) => {
    setQuery(`page=1&size=50`);

    setTableParams((prev) => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        total: count,
        current: 1,
      },
    }));

    setDate(dateString);
  };

  const handleSearch = (e: any) => {
    setQuery(`page=1&size=50`);

    setTableParams((prev) => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        total: count,
        current: 1,
      },
    }));

    setSearch(removeSpaceBeforeWord(e.target.value));
  };

  const invoiceList = filterNullColumns(dataSource, columns);

  return (
    <>
      <Row
        gutter={{ xs: 8, sm: 16, md: 24, lg: 10 }}
        style={{ marginBottom: '25px' }}
        justify={'space-between'}
      >
        <Col span={12}>
          <Row gutter={10}>
            {permission?.['create:any'] && (
              <Col xs={24} sm={12} md={8} lg={8}>
                <Link to={createLink}>
                  <Button
                    type='primary'
                    icon={<ArrowLeftOutlined />}
                    style={{ width: '100%' }}
                  >
                    Create
                  </Button>
                </Link>
              </Col>
            )}

            <Col xs={24} sm={12} md={8} lg={8} style={{ display: 'flex' }}>
              <Col>
                <Reload
                  refetch={() => {
                    refetch({});
                  }}
                />
              </Col>
            </Col>
          </Row>
        </Col>

        <Col span={12}>
          <Row justify={'end'} gutter={10}>
            <Col xs={24} sm={12} md={8} lg={8}>
              <DatePicker.RangePicker
                presets={rangePresets}
                format={'YYYY-MM-DD'}
                onChange={handleDateRangeChange}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Input
                placeholder='🔍 Search Invoices...'
                onBlur={handleSearch}
                onPressEnter={handleSearch}
                allowClear
              ></Input>
            </Col>
          </Row>
        </Col>
      </Row>

      <Table
        size='small'
        bordered
        // rowKey={(e) => Math.random()}
        rowClassName={(record) => {
          const {
            invoice_is_reissued: reissue,
            invoice_is_refund: refund,
            invoice_is_void: void_inv,
          } = record;
          let className = '';

          if (void_inv) return (className = 'void_inv-row-style');
          if (reissue) return (className = 'reissued-row-style');
          if (refund) return (className = 'refund-row-style');

          return className;
        }}
        loading={{
          spinning: isLoading,
          indicator: loadingIndicator,
        }}
        columns={invoiceList.col}
        scroll={{ x: true }}
        dataSource={dataSource}
        pagination={count > 50 ? tableParams.pagination : false}
        onChange={(args1) => {
          setTableParams((prev) => ({ ...prev, pagination: args1 }));
          setQuery &&
            args1.current &&
            args1?.pageSize &&
            setQuery(`page=${args1?.current}&size=${args1?.pageSize}`);
        }}
      />
    </>
  );
};

export default InvoiceListHeader;

export const isMoallem =
  location.pathname === '/invoiceumrah' ||
  location.pathname === '/invoicehajj' ||
  location.pathname === '/hajjpreregistration';

export function removeSpaceBeforeWord(inputString: string) {
  let words = inputString.split(' ');
  let result = words.filter((word) => word !== '').join(' ');
  return result;
}
